import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Image, ListGroup, Card, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useGetProductDetailsQuery, useCreateReviewMutation } from '../slices/productsApiSlice';
import Rating from '../components/Rating';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Meta from '../components/Meta';
import { addToCart } from '../slices/cartSlice';


const ProductScreen = () => {
  const { id: productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const [qty, setQty] = useState(1);
  const [color, setColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const { data: product, isLoading, refetch, error } = useGetProductDetailsQuery(productId);
  const [createReview, { isLoading: loadingProductReview }] = useCreateReviewMutation();

  // Initialize default variant after product loads
  useEffect(() => {
    if (!product?.variants?.length) return;

    const defaultVariant = product.variants[0];
    setSelectedSize(defaultVariant.size);
    setSelectedImage(defaultVariant.images?.[0] || product.images?.[0]);
  }, [product]);

  const selectedVariant = product?.variants?.find(v => v.size === selectedSize);
  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;
  const mainImage = selectedImage || selectedVariant?.images?.[0] || product?.images?.[0];

  const addToCartHandler = () => {
    if (!selectedVariant) return;

    const variant = {
      size: selectedVariant.size,
      color: color || '', // optional color
      price: selectedVariant.price,
      images: selectedVariant.images,
      countInStock: selectedVariant.countInStock,
    };

    dispatch(
      addToCart({
        ...product,
        qty,
        variant,
      })
    );

    navigate('/cart');
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await createReview({ productId, rating, comment }).unwrap();
      refetch();
      toast.success('Review submitted!');
      setRating(0);
      setComment('');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <Link className="btn btn-light my-3" to="/">Go Back</Link>

      {isLoading ? <Loader /> :
       error ? <Message variant="danger">{error?.data?.message || error.error}</Message> :
       <>
         <Meta title={product.name} />

         <Row>
           <Col md={6}>
             {mainImage && <Image src={mainImage} alt={product.name} fluid />}
             <Row className="mt-3">
               {selectedVariant?.images?.map((img, index) => (
                 <Col xs={2} key={index}>
                   <Image
                     src={img}
                     alt={`Thumbnail ${index}`}
                     fluid
                     thumbnail
                     style={{ cursor: 'pointer', border: mainImage === img ? '2px solid #007bff' : '1px solid #dee2e6' }}
                     onClick={() => setSelectedImage(img)}
                   />
                 </Col>
               ))}
             </Row>
           </Col>

           <Col md={3}>
             <ListGroup variant="flush">
               <ListGroup.Item><h3>{product.name}</h3></ListGroup.Item>
               <ListGroup.Item>
                 {(selectedVariant?.description?.length ? selectedVariant.description : product.description)?.map((line, idx) => (
                   <p key={idx}>{line}</p>
                 ))}
               </ListGroup.Item>
                <ListGroup.Item>
                  <Rating
                    value={product.rating}
                    text={`${product.numReviews} reviews`}
                  />
                </ListGroup.Item>
             </ListGroup>
           </Col>

           <Col md={3}>
             <Card>
               <ListGroup variant="flush">
                 <ListGroup.Item>
                   <Row>
                     <Col><strong>Price:</strong></Col>
                     <Col><strong>${displayPrice.toFixed(2)}</strong></Col>
                   </Row>
                 </ListGroup.Item>

                 {product.variants?.length > 0 && (
                   <ListGroup.Item>
                     <strong>Select Size / Variant</strong>
                     <Form.Control
                       as="select"
                       value={selectedSize}
                       onChange={(e) => {
                         const size = e.target.value;
                         setSelectedSize(size);
                         const variant = product.variants.find(v => v.size === size);
                         setSelectedImage(variant?.images?.[0] || product.images?.[0]);
                       }}
                       className="mt-2"
                     >
                       {product.variants.map((v) => (
                         <option key={v.size} value={v.size}>{v.size}</option>
                       ))}
                     </Form.Control>
                   </ListGroup.Item>
                 )}

                 {selectedVariant?.countInStock > 0 && (
                   <ListGroup.Item>
                     <Row>
                       <Col><strong>Qty</strong></Col>
                       <Col>
                         <Form.Control
                           as="select"
                           value={qty}
                           onChange={e => setQty(Number(e.target.value))}
                         >
                           {[...Array(selectedVariant.countInStock).keys()].map(x => (
                             <option key={x + 1} value={x + 1}>{x + 1}</option>
                           ))}
                         </Form.Control>
                       </Col>
                     </Row>
                   </ListGroup.Item>
                 )}

                 <ListGroup.Item>
                   <Button
                     className="btn-block"
                     type="button"
                     disabled={!selectedVariant || selectedVariant.countInStock === 0}
                     onClick={addToCartHandler}
                   >
                     Add To Cart
                   </Button>
                 </ListGroup.Item>
               </ListGroup>
             </Card>
           </Col>
         </Row>

 <Row className='review'>
            <Col md={6}>
              <h2>Reviews</h2>
              {product.reviews.length === 0 && <Message>No Reviews</Message>}
              <ListGroup variant='flush'>
                {product.reviews.map((review) => (
                  <ListGroup.Item key={review._id}>
                    <strong>{review.name}</strong>
                    <Rating value={review.rating} />
                    <p>{review.createdAt.substring(0, 10)}</p>
                    <p>{review.comment}</p>
                  </ListGroup.Item>
                ))}
                <ListGroup.Item>
                  <h2>Write a Customer Review</h2>

                  {loadingProductReview && <Loader />}

                  {userInfo ? (
                    <Form onSubmit={submitHandler}>
                      <Form.Group className='my-2' controlId='rating'>
                        <Form.Label>Rating</Form.Label>
                        <Form.Control
                          as='select'
                          required
                          value={rating}
                          onChange={(e) => setRating(e.target.value)}
                        >
                          <option value=''>Select...</option>
                          <option value='1'>1 - Poor</option>
                          <option value='2'>2 - Fair</option>
                          <option value='3'>3 - Good</option>
                          <option value='4'>4 - Very Good</option>
                          <option value='5'>5 - Excellent</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group className='my-2' controlId='comment'>
                        <Form.Label>Comment</Form.Label>
                        <Form.Control
                          as='textarea'
                          row='3'
                          required
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                        ></Form.Control>
                      </Form.Group>
                      <Button
                        disabled={loadingProductReview}
                        type='submit'
                        variant='primary'
                      >
                        Submit
                      </Button>
                    </Form>
                  ) : (
                    <Message>
                      Please <Link to='/login'>sign in</Link> to write a review
                    </Message>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
        




       </>
      }
    </>
  );
};

export default ProductScreen;